import React, { useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Brain, Zap, Trophy } from 'lucide-react';
import { motion } from 'framer-motion';
import QuizWidget from './QuizWidget';
import { useAuth } from '../contexts/AuthContext';
import AuthModal from './AuthModal';

/**
 * QuizCTABanner — reusable quiz invitation banner.
 * Variants: "sidebar" | "inline" | "hero"
 */
export default function QuizCTABanner({ variant = 'sidebar', className = '' }) {
  const { user } = useAuth();
  const [showQuiz, setShowQuiz] = useState(false);
  const [showAuth, setShowAuth] = useState(false);

  const handleClick = () => {
    if (!user) { setShowAuth(true); return; }
    setShowQuiz(true);
  };

  if (variant === 'sidebar') {
    return (
      <>
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className={className}
        >
          <Card
            className="fly-card p-4 cursor-pointer hover:shadow-md transition-all group border-[#7C3AED]/20 bg-gradient-to-br from-[#7C3AED]/5 to-[#FF4500]/5"
            onClick={handleClick}
            data-testid="quiz-cta-sidebar"
          >
            <div className="flex items-start gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-[#7C3AED]/15 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                <Brain className="w-5 h-5 text-[#7C3AED]" />
              </div>
              <div>
                <p className="text-sm font-bold" style={{ fontFamily: 'Space Grotesk' }}>Daily Travel Quiz</p>
                <p className="text-[10px] text-muted-foreground mt-0.5">5 questions · Earn XP</p>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mb-3 leading-relaxed">
              How well do you know the skies? Earn up to <strong className="text-[#FF4500]">35 XP</strong> today.
            </p>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1 text-[10px] text-amber-500 font-semibold">
                <Trophy className="w-3 h-3" /> Up to 35 XP
              </div>
              <Button size="sm" className="h-7 px-3 text-xs fly-btn-primary" data-testid="quiz-cta-start-btn">
                Take Quiz
              </Button>
            </div>
          </Card>
        </motion.div>
        {showQuiz && <QuizWidget autoOpen hideCard />}
        <AuthModal open={showAuth} onOpenChange={setShowAuth} />
      </>
    );
  }

  if (variant === 'inline') {
    return (
      <>
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className={className}
        >
          <Card
            className="fly-card p-4 cursor-pointer hover:shadow-md transition-all group border-[#7C3AED]/20 bg-gradient-to-r from-[#7C3AED]/8 via-background to-[#FF4500]/5"
            onClick={handleClick}
            data-testid="quiz-cta-inline"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-[#7C3AED]/15 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                <Brain className="w-6 h-6 text-[#7C3AED]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold" style={{ fontFamily: 'Space Grotesk' }}>
                  Daily Travel Quiz — Test your aviation knowledge!
                </p>
                <p className="text-xs text-muted-foreground">5 questions · Earn up to <span className="text-[#FF4500] font-semibold">35 XP</span></p>
              </div>
              <Button size="sm" className="fly-btn-primary hidden sm:flex items-center gap-1.5 flex-shrink-0" data-testid="quiz-cta-inline-btn">
                <Zap className="w-3.5 h-3.5" /> Start
              </Button>
            </div>
          </Card>
        </motion.div>
        {showQuiz && <QuizWidget autoOpen hideCard />}
        <AuthModal open={showAuth} onOpenChange={setShowAuth} />
      </>
    );
  }

  // hero variant
  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        className={className}
      >
        <Card
          className="fly-card p-6 sm:p-8 cursor-pointer hover:shadow-lg transition-all group border-[#7C3AED]/20 overflow-hidden relative"
          onClick={handleClick}
          data-testid="quiz-cta-hero"
          style={{ background: 'linear-gradient(135deg, rgba(124,58,237,0.08) 0%, rgba(255,69,0,0.05) 100%)' }}
        >
          <div className="absolute top-0 right-0 w-40 h-40 rounded-full bg-[#7C3AED]/5 -translate-y-1/2 translate-x-1/2 pointer-events-none" />
          <div className="absolute bottom-0 left-0 w-24 h-24 rounded-full bg-[#FF4500]/5 translate-y-1/2 -translate-x-1/2 pointer-events-none" />
          <div className="relative flex flex-col sm:flex-row items-center gap-6">
            <div className="w-16 h-16 rounded-2xl bg-[#7C3AED]/15 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
              <Brain className="w-8 h-8 text-[#7C3AED]" />
            </div>
            <div className="text-center sm:text-left flex-1">
              <p className="text-lg font-bold mb-1" style={{ fontFamily: 'Space Grotesk' }}>Daily Aviation Quiz</p>
              <p className="text-sm text-muted-foreground mb-4 max-w-md">
                Think you know your airports and airlines? Take today's 5-question challenge and earn XP toward your traveler rank.
              </p>
              <div className="flex flex-wrap gap-3 justify-center sm:justify-start">
                <Button className="fly-btn-primary gap-2" data-testid="quiz-cta-hero-btn">
                  <Zap className="w-4 h-4" /> Take Today's Quiz
                </Button>
                <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-amber-500/10 border border-amber-500/20">
                  <Trophy className="w-4 h-4 text-amber-500" />
                  <span className="text-sm font-semibold text-amber-600">Up to 35 XP</span>
                </div>
              </div>
            </div>
          </div>
        </Card>
      </motion.div>
      {showQuiz && <QuizWidget autoOpen hideCard />}
      <AuthModal open={showAuth} onOpenChange={setShowAuth} />
    </>
  );
}
